import { useCallback, useEffect, useReducer, useState } from "react";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { useComplianceRevision } from "./complianceLive";
import {
  decodeInspectionDetail,
  decodeInspectionList,
  type InspectionDetail,
  inspectionActive,
} from "./inspectionSchemas";

export interface InspectionResource<T> {
  scope: string;
  snapshot: T | null;
  error: string | null;
  missing: boolean;
  refreshedAt: number | null;
}
/**
 * The safety refresh, not the update path: a run starting, settling or being
 * stopped is pushed on the compliance cursor (`inspectorRunEvents.ts` on the
 * server), which re-runs every reader through `useComplianceRevision`. This only
 * covers a push that never arrived.
 */
export const inspectionRefreshDelay = (active: boolean, history: boolean) =>
  active ? 15_000 : history ? 60_000 : null;

/** One sequential read loop. Retain confirmed data on failure, pause in the background, stop finished detail reads. */
export function useInspectionResource<T>(
  organizationId: string | null,
  scope: string,
  params: Record<string, string> | null,
  decode: (value: unknown) => T,
  active: (value: T) => boolean,
  history: boolean,
) {
  const revision = useComplianceRevision(organizationId);
  const [version, refresh] = useReducer((n: number) => n + 1, 0);
  const [state, setState] = useState<InspectionResource<T>>({
    scope,
    snapshot: null,
    error: null,
    missing: false,
    refreshedAt: null,
  });
  // A tenant/route switch cannot show the previous response for even one render.
  const current =
    state.scope === scope
      ? state
      : { scope, snapshot: null, error: null, missing: false, refreshedAt: null };
  const serialized = params ? JSON.stringify(params) : null;
  useEffect(() => {
    if (!organizationId || !serialized) return;
    let disposed = false;
    let controller: AbortController | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      clearTimeout(timer);
      controller?.abort();
      if (document.visibilityState === "hidden" || disposed) return;
      const request = new AbortController();
      controller = request;
      let delay: number | null = 15_000;
      try {
        const response = await fetch(
          resolvePrimaryEnvironmentHttpUrl("/api/amoreg/inspector/runs", JSON.parse(serialized)),
          { credentials: "include", signal: request.signal },
        );
        if (request.signal.aborted || disposed) return;
        if (response.status === 401 || response.status === 403) {
          setState({
            scope,
            snapshot: null,
            error: "You no longer have access to these inspections.",
            missing: false,
            refreshedAt: null,
          });
          delay = null;
          return;
        }
        if (response.status === 404 && !history) {
          setState({ scope, snapshot: null, error: null, missing: true, refreshedAt: Date.now() });
          delay = null;
          return;
        }
        if (!response.ok)
          throw new Error(
            "The inspection could not be refreshed. Your last update is still shown.",
          );
        const value = decode(await response.json());
        if (request.signal.aborted || disposed) return;
        setState({ scope, snapshot: value, error: null, missing: false, refreshedAt: Date.now() });
        delay = inspectionRefreshDelay(active(value), history);
      } catch {
        if (!request.signal.aborted && !disposed)
          setState((previous) => ({
            scope,
            snapshot: previous.scope === scope ? previous.snapshot : null,
            error: "Could not refresh inspections. Try again or wait for the next update.",
            missing: false,
            refreshedAt: previous.scope === scope ? previous.refreshedAt : null,
          }));
      } finally {
        if (!request.signal.aborted && !disposed && delay !== null)
          timer = setTimeout(() => void load(), delay);
      }
    };
    const visibility = () => {
      clearTimeout(timer);
      if (document.visibilityState === "hidden") controller?.abort();
      else void load();
    };
    void load();
    document.addEventListener("visibilitychange", visibility);
    return () => {
      disposed = true;
      controller?.abort();
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [organizationId, scope, serialized, decode, active, history, revision, version]);
  return {
    ...current,
    refresh: useCallback(() => refresh(), []),
    loading: current.snapshot === null && current.error === null && !current.missing,
  };
}
const liveList = (value: ReturnType<typeof decodeInspectionList>) =>
  value.runs.some(inspectionActive);
const liveDetail = (value: InspectionDetail) => inspectionActive(value.run);
export function useInspections(organizationId: string | null, findingId?: string) {
  return useInspectionResource(
    organizationId,
    JSON.stringify([organizationId, findingId ?? null]),
    organizationId ? { organizationId, ...(findingId ? { findingId } : {}) } : null,
    decodeInspectionList,
    liveList,
    true,
  );
}
export function useInspection(organizationId: string | null, threadId: string) {
  return useInspectionResource(
    organizationId,
    JSON.stringify([organizationId, threadId]),
    organizationId ? { organizationId, threadId } : null,
    decodeInspectionDetail,
    liveDetail,
    false,
  );
}

/**
 * The newest re-assessment of one document, for the page watching it. The
 * server filters by document before limiting, so other documents' runs cannot
 * crowd this one out. Not a history read: when nothing is running there is no
 * timer at all, and the list is re-read only when the compliance cursor says
 * something moved — a version captured, a run started, a turn settled.
 */
export function useReassessments(organizationId: string | null, policyId: string) {
  return useInspectionResource(
    organizationId,
    JSON.stringify([organizationId, "reassessment", policyId]),
    organizationId
      ? { organizationId, trigger: "reassessment", policyId, limit: "1" }
      : null,
    decodeInspectionList,
    liveList,
    false,
  );
}
